import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { PlainLink as Link } from 'infrastructure/routing'
import { SSDetails } from './SSDetails'
import { FrameworkDetails } from './FrameworkDetails'
import {
  TreeStructureValidation,
  CrosswalkValidation,
  ScopeSequenceValidation,
  LessonPoolValidation,
} from './Validations'
import { TreeStructureDetails } from './TreeStructureDetails'
import { LessonPoolDetails } from './LessonPoolDetails'
import {
  useScopeAndSequenceUrlParams,
  useTreeSourceMode,
} from './useScopeAndSequenceUrlParams'
import { SpreadsheetWorkflow } from './SpreadsheetWorkflow'

const ScopeAndSequenceSteps: [React.FC, React.FC, string][] = [
  [ScopeSequenceValidation, SSDetails, 's&s'],
  [CrosswalkValidation, FrameworkDetails, 'crosswalk'],
  [LessonPoolValidation, LessonPoolDetails, 'lessonPools'],
  [TreeStructureValidation, TreeStructureDetails, 'treeStructure'],
]

export function ScopeAndSequence() {
  useScopeAndSequenceUrlParams()
  const { isSpreadsheet, switchToManual, switchToSpreadsheetPending } =
    useTreeSourceMode()

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', p: 3, minHeight: '100vh' }}>
      <Button component={Link} to="/scope_and_sequence" size="small">
        &larr; Back to List
      </Button>
      <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
        COURSE BUILDER
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Create a new state adoption course
      </Typography>

      <WorkflowModeSelector
        isSpreadsheet={isSpreadsheet}
        onSwitchToManual={switchToManual}
        onSwitchToSpreadsheet={switchToSpreadsheetPending}
      />

      {isSpreadsheet ? <SpreadsheetWorkflow /> : <ManualWorkflow />}
    </Box>
  )
}

function WorkflowModeSelector({
  isSpreadsheet,
  onSwitchToManual,
  onSwitchToSpreadsheet,
}: {
  isSpreadsheet: boolean
  onSwitchToManual: () => void
  onSwitchToSpreadsheet: () => void
}) {
  const handleModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: string | null,
  ) => {
    if (newMode === 'manual') {
      onSwitchToManual()
    } else if (newMode === 'spreadsheet') {
      onSwitchToSpreadsheet()
    }
  }

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        backgroundColor: '#fff',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Workflow Mode
          </Typography>
          <ToggleButtonGroup
            value={isSpreadsheet ? 'spreadsheet' : 'manual'}
            exclusive
            onChange={handleModeChange}
            size="small"
          >
            <ToggleButton value="manual">Start Fresh (Manual)</ToggleButton>
            <ToggleButton value="spreadsheet">
              Import from Spreadsheet
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {isSpreadsheet && (
          <Typography variant="body2" color="success.main">
            Working from imported spreadsheet
          </Typography>
        )}
      </Box>
    </Box>
  )
}

function ManualWorkflow() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {ScopeAndSequenceSteps.map(([Validation, Details, key]) => {
        return (
          <Accordion
            key={key}
            variant="outlined"
            sx={{ borderRadius: 2, '&:before': { display: 'none' } }}
          >
            <AccordionSummary>
              <Validation />
            </AccordionSummary>
            <AccordionDetails>
              <Details />
            </AccordionDetails>
          </Accordion>
        )
      })}
    </Box>
  )
}
